import React, { useEffect, useRef, useState } from 'react'
import styles from './EventGroupMaintenance.module.css'
import { useParams } from "react-router"
import axios from "axios"
import LoadingBackdrop from "../../components/common/LoadingBackdrop"
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    TextField,
    MenuItem,
    Select,
    SelectChangeEvent,
    Button,
    useMediaQuery,
} from "@mui/material"
import { useAutoEvt } from "../../queries/useAutoEvt"

interface Params {
    eventid: string
}

export interface DataItem {
    ticketid: number | string
    ticketname?: string
    groupid?: number | string
    transactionname?: string
}

const SCROLL_ZONE = 100  // px from viewport edge to begin scrolling
const SCROLL_SPEED = 12  // max px per animation frame

type LayoutStyle = 'grid' | 'masonry'

const cx = (...classes: (string | false | null | undefined)[]) => classes.filter(Boolean).join(' ')

const DotsIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor"><circle cx="9" cy="5" r="1.5" /><circle cx="15" cy="5" r="1.5" /><circle cx="9" cy="12" r="1.5" /><circle cx="15" cy="12" r="1.5" /><circle cx="9" cy="19" r="1.5" /><circle cx="15" cy="19" r="1.5" /></svg>
)
const EditIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
)
const DeleteIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" /></svg>
)
const GridIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></svg>
)
const MasonryIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="9" /><rect x="14" y="3" width="7" height="5" /><rect x="3" y="16" width="7" height="5" /><rect x="14" y="12" width="7" height="9" /></svg>
)

const isDefaultGroup = (group: any) => group?.allowchangegroupname === false

// Default group always sits in position 1
const withDefaultFirst = (groups: any[]): any[] => {
    const defIdx = groups.findIndex(isDefaultGroup)
    if (defIdx <= 0) return groups
    const reordered = [...groups]
    const [def] = reordered.splice(defIdx, 1)
    reordered.unshift(def)
    return reordered
}

// Best guess at a "full" group: median size of the non-default groups that have guests
const calculateMedianGroupSize = (groups: any[]): number => {
    const sizes = groups
        .filter(g => !isDefaultGroup(g) && g.guests?.length > 0)
        .map(g => g.guests.length)
    if (sizes.length === 0) return 0
    sizes.sort((a, b) => a - b)
    const mid = Math.floor(sizes.length / 2)
    return sizes.length % 2 === 0
        ? Math.round((sizes[mid - 1] + sizes[mid]) / 2)
        : sizes[mid]
}

const EventGroupMaintenance = () => {
    const { eventid } = useParams<Params>()
    useAutoEvt(eventid)

    const [dialogObj, setDialogObj] = useState({ msg: "", id: 0 })
    const [dialogMsg, setDialogMsg] = useState("")
    const [groupName, setGroupName] = useState({ name: "", groupid: 0 })
    const [newGroupName, setNewGroupName] = useState("")
    const [loading, setLoading] = useState(false)
    const [ticketItem, setTicketItem] = useState("-1")
    const [groupData, setGroupData] = useState({
        ticketgroups: [],
        ticketitemlist: [],
        selectedticketitemid: 0,
    })
    const [layoutStyle, setLayoutStyle] = useState<LayoutStyle>('grid')
    const [groupSize, setGroupSize] = useState(0)
    const [groupSizeInput, setGroupSizeInput] = useState('0')
    const [collapseGroups, setCollapseGroups] = useState(false)
    const groupSizeGuessedFor = useRef<number | null>(null)

    const isNarrow = useMediaQuery('(max-width: 800px)')
    const isPhone = useMediaQuery('(max-width: 520px)')
    const columnCount = isPhone ? 1 : isNarrow ? 2 : 3

    // Guest drag state
    const [draggingItem, setDraggingItem] = useState<DataItem | null>(null)
    const [overGroupId, setOverGroupId] = useState<number | string | null>(null)
    const dragItemRef = useRef<DataItem | null>(null)

    // Group reorder drag state
    const [draggingGroupId, setDraggingGroupId] = useState<number | string | null>(null)
    const draggingGroupIdRef = useRef<number | string | null>(null)
    const [overGroupIdForReorder, setOverGroupIdForReorder] = useState<number | string | null>(null)

    // Auto-scroll refs
    const scrollAnimRef = useRef<number | null>(null)
    const dragClientYRef = useRef<number>(0)

    useEffect(() => {
        getGroups()
    }, [])

    useEffect(() => {
        setTicketItem(groupData.selectedticketitemid.toString())
    }, [groupData.selectedticketitemid])

    const [reloadTrigger, setReloadTrigger] = useState(0)
    const triggerReload = () => setReloadTrigger(n => n + 1)

    useEffect(() => {
        if (reloadTrigger > 0) getGroups()
    }, [reloadTrigger])

    useEffect(() => {
        if (ticketItem !== "-1") getGroups()
    }, [ticketItem])

    const handleSelectionChange = (event: SelectChangeEvent) => {
        setTicketItem(event.target.value as string)
    }

    const getGroups = async () => {
        const body: any = { eventid }
        if (Number(ticketItem) > 0) body.ticketitemid = Number(ticketItem)
        setLoading(true)
        try {
            const { data } = await axios.post('/evtactivitygroups', body)
            const parsedData = JSON.parse(data.jsonString)
            parsedData.ticketgroups = withDefaultFirst(parsedData.ticketgroups || [])
            setGroupData(parsedData)
            // Guess the group size once per ticket item; later reloads (drag/drop) keep the current value
            if (groupSizeGuessedFor.current !== parsedData.selectedticketitemid) {
                groupSizeGuessedFor.current = parsedData.selectedticketitemid
                const size = calculateMedianGroupSize(parsedData.ticketgroups)
                setGroupSize(size)
                setGroupSizeInput(String(size))
            }
        } finally {
            setLoading(false)
        }
    }

    const commitGroupSize = () => {
        const size = Math.max(0, parseInt(groupSizeInput, 10) || 0)
        setGroupSizeInput(String(size))
        setGroupSize(size)
    }

    const getTicketItemOptions = () =>
        groupData?.ticketitemlist?.map(item => (
            <MenuItem key={item.ticketitemid} value={item.ticketitemid}>
                {item.ticketitemname}
            </MenuItem>
        ))

    const stopAutoScroll = () => {
        if (scrollAnimRef.current !== null) {
            cancelAnimationFrame(scrollAnimRef.current)
            scrollAnimRef.current = null
        }
    }

    const startAutoScroll = () => {
        stopAutoScroll()
        const step = () => {
            const y = dragClientYRef.current
            const viewH = window.innerHeight
            if (y < SCROLL_ZONE) {
                window.scrollBy(0, -SCROLL_SPEED * (1 - y / SCROLL_ZONE))
            } else if (y > viewH - SCROLL_ZONE) {
                window.scrollBy(0, SCROLL_SPEED * ((y - (viewH - SCROLL_ZONE)) / SCROLL_ZONE))
            }
            scrollAnimRef.current = requestAnimationFrame(step)
        }
        scrollAnimRef.current = requestAnimationFrame(step)
    }

    const handleDragStart = (e: React.DragEvent, item: DataItem) => {
        dragItemRef.current = item
        dragClientYRef.current = e.clientY
        e.dataTransfer.effectAllowed = 'move'
        setTimeout(() => setDraggingItem(item), 0)
        startAutoScroll()
    }

    const handleDrag = (e: React.DragEvent) => {
        // clientY is 0 on the final synthetic drag event before drop — ignore it
        if (e.clientY !== 0) dragClientYRef.current = e.clientY
    }

    const handleDragEnd = () => {
        dragItemRef.current = null
        setDraggingItem(null)
        setOverGroupId(null)
        stopAutoScroll()
    }

    const handleGroupHeaderDragStart = (e: React.DragEvent, groupId: number | string) => {
        const group = groupData.ticketgroups.find(g => g.groupid === groupId)
        if (group?.allowchangegroupname === false) return  // default group stays at position 1
        e.stopPropagation()
        e.dataTransfer.setData('text/plain', String(groupId))
        e.dataTransfer.effectAllowed = 'move'
        draggingGroupIdRef.current = groupId
        dragClientYRef.current = e.clientY
        setTimeout(() => setDraggingGroupId(groupId), 0)
        startAutoScroll()
    }

    const handleGroupHeaderDragEnd = () => {
        draggingGroupIdRef.current = null
        setDraggingGroupId(null)
        setOverGroupIdForReorder(null)
        stopAutoScroll()
    }

    const handleGroupDragOver = (e: React.DragEvent, groupId: number | string) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        if (e.clientY !== 0) dragClientYRef.current = e.clientY
        if (draggingGroupIdRef.current !== null) {
            setOverGroupIdForReorder(groupId)
        } else {
            setOverGroupId(groupId)
        }
    }

    const handleGroupDragLeave = (e: React.DragEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setOverGroupId(null)
            setOverGroupIdForReorder(null)
        }
    }

    const handleGroupDrop = async (e: React.DragEvent, targetGroupId: number | string) => {
        e.preventDefault()
        setOverGroupId(null)
        setOverGroupIdForReorder(null)
        stopAutoScroll()

        if (draggingGroupIdRef.current !== null) {
            const sourceGroupId = draggingGroupIdRef.current
            draggingGroupIdRef.current = null
            setDraggingGroupId(null)

            if (sourceGroupId === targetGroupId) return

            const current = groupData.ticketgroups
            const fromIdx = current.findIndex(g => g.groupid === sourceGroupId)
            const toIdx = current.findIndex(g => g.groupid === targetGroupId)
            if (fromIdx === -1 || toIdx === -1) return

            const moved = [...current]
            const [movedGroup] = moved.splice(fromIdx, 1)
            moved.splice(toIdx, 0, movedGroup)
            const reordered = withDefaultFirst(moved)

            setGroupData(prev => ({ ...prev, ticketgroups: reordered }))

            setLoading(true)
            try {
                await axios.put('/evtactivitygrouporder', {
                    eventid: Number(eventid),
                    ticketitemid: Number(ticketItem),
                    grouporder: reordered.map(g => Number(g.groupid))
                })
                triggerReload()
            } catch {
                setDialogMsg('Failed to save group order. Please try again.')
                triggerReload()
            } finally {
                setLoading(false)
            }
            return
        }

        const item = dragItemRef.current
        dragItemRef.current = null
        setDraggingItem(null)

        if (!item || item.groupid === targetGroupId) return

        setGroupData(prev => {
            const groups = prev.ticketgroups.map(g => ({
                ...g,
                guests: (g.guests || []).filter(guest => guest.ticketid !== item.ticketid)
            }))
            return {
                ...prev,
                ticketgroups: groups.map(g =>
                    g.groupid === targetGroupId
                        ? { ...g, guests: [...(g.guests || []), { ticketid: item.ticketid, ticketname: item.ticketname, transactionname: item.transactionname }] }
                        : g
                )
            }
        })

        setLoading(true)
        try {
            await axios.put('/evtactivitygroups', {
                eventid,
                ticketitemid: ticketItem,
                groupid: targetGroupId,
                ticketlist: [{ ticketid: item.ticketid }]
            })
            triggerReload()
        } catch {
            setDialogMsg('Failed to move item. Please try again.')
            triggerReload()
        } finally {
            setLoading(false)
        }
    }

    const handleEditGroup = (group) => {
        setNewGroupName(group.groupname)
        setGroupName({ name: group.groupname, groupid: Number(group.groupid) })
    }

    const addNewGroup = () => {
        setNewGroupName('New Group Name')
        setGroupName({ name: 'New Group Name', groupid: -1 })
    }

    const saveGroupName = async () => {
        const finaldata = {
            eventid: Number(eventid),
            groupname: newGroupName,
            groupid: groupName.groupid,
            ticketitemid: Number(ticketItem),
        }
        const { data } = await axios.put('/evtactivitygroupname', finaldata)
        const json = JSON.parse(data.jsonString)
        if (json.status === "failure") {
            setGroupName({ name: '', groupid: 0 })
            setDialogMsg(json.message)
        } else {
            setNewGroupName('')
            setGroupName({ name: '', groupid: 0 })
            triggerReload()
        }
    }

    const handleDeleteGroup = (group) => {
        setDialogObj({
            msg: `Are you sure you want to delete "${group.groupname}"?`,
            id: Number(group.groupid)
        })
    }

    const handleDeleteConfirm = async () => {
        const finaldata = {
            eventid: Number(eventid),
            groupid: dialogObj.id,
            ticketitemid: Number(ticketItem),
            delete: true
        }
        const { data } = await axios.delete('/evtactivitygroupname', { data: finaldata })
        const json = JSON.parse(data.jsonString)
        if (json.status === "failure") {
            setDialogObj({ msg: '', id: 0 })
            setDialogMsg(json.message)
        } else {
            setDialogObj({ msg: '', id: 0 })
            triggerReload()
        }
    }

    const closeDialog = () => {
        setGroupName({ name: '', groupid: 0 })
        setDialogMsg('')
        setDialogObj({ msg: '', id: 0 })
    }

    // Group size 0 = no defined capacity, so "collapse" applies to every non-default group
    const isGroupCollapsed = (group) =>
        collapseGroups && !isDefaultGroup(group) && (groupSize > 0 ? group.guests.length >= groupSize : true)

    const renderGroupCard = (group) => {
        const isOver = overGroupId === group.groupid
        const isOverForReorder = overGroupIdForReorder === group.groupid
        const isBeingDragged = draggingGroupId === group.groupid
        const visibleGuests = group.guests.filter(g => g.ticketid !== draggingItem?.ticketid)
        const isDefault = isDefaultGroup(group)
        // A collapsed group opens while an item is dragged over it so you can see where it lands
        const isCollapsed = isGroupCollapsed(group) && !(isOver && draggingItem)
        const count = visibleGuests.length
        // With a group size set, show fill as "3/8"; the holding group keeps its unassigned count
        const countLabel = isDefault
            ? `${count} unassigned`
            : groupSize > 0
                ? `${count}/${groupSize}`
                : `${count} ${count === 1 ? 'item' : 'items'}`
        // Collapsed cards hide their items, so flag Full/Over to tell them apart from empty groups
        const fillStatus = isCollapsed && !isDefault && groupSize > 0 && count >= groupSize
            ? (count > groupSize ? 'Over' : 'Full')
            : null

        return (
            <div
                key={group.groupid}
                className={cx(
                    styles.card,
                    isDefault && styles.isDefault,
                    isOver && draggingItem?.groupid !== group.groupid && styles.dropTarget,
                    isOverForReorder && !isBeingDragged && !isDefault && styles.groupDropBefore,
                    isBeingDragged && styles.draggingGroup,
                )}
                onDragEnter={(e) => e.preventDefault()}
                onDragOver={(e) => handleGroupDragOver(e, group.groupid)}
                onDragLeave={handleGroupDragLeave}
                onDrop={(e) => handleGroupDrop(e, group.groupid)}
            >
                <div className={styles.cardHead}>
                    {isDefault ? (
                        <span className={cx(styles.groupHandle, styles.disabled)}><DotsIcon /></span>
                    ) : (
                        <span
                            className={styles.groupHandle}
                            draggable
                            onDragStart={(e) => handleGroupHeaderDragStart(e, group.groupid)}
                            onDrag={handleDrag}
                            onDragEnd={handleGroupHeaderDragEnd}
                            title="Drag to reorder groups"
                        >
                            <DotsIcon />
                        </span>
                    )}
                    <h3>{group.groupname}</h3>
                    {fillStatus && (
                        <span className={cx(styles.fillBadge, fillStatus === 'Over' && styles.fillOver)}>
                            {fillStatus}
                        </span>
                    )}
                    {isDefault && <span className={styles.pin}>Holding group</span>}
                    {!isDefault && (
                        <div className={styles.cardActions}>
                            <button title="Rename group" onClick={() => handleEditGroup(group)}>
                                <EditIcon />
                            </button>
                            <button
                                title="Delete group"
                                disabled={visibleGuests.length > 0}
                                onClick={() => handleDeleteGroup(group)}
                            >
                                <DeleteIcon />
                            </button>
                        </div>
                    )}
                </div>

                {isCollapsed ? (
                    <div className={styles.collapsedBody}>
                        {countLabel}
                    </div>
                ) : (
                    <>
                        <div className={styles.meta}>
                            {countLabel}
                        </div>
                        <div className={styles.items}>
                            {group.guests.map((item) => {
                                const showPurchaser = item.transactionname && item.transactionname !== item.ticketname
                                return (
                                    <div
                                        key={item.ticketid}
                                        className={cx(styles.item, draggingItem?.ticketid === item.ticketid && styles.dragging)}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, { ...item, groupid: group.groupid })}
                                        onDrag={handleDrag}
                                        onDragEnd={handleDragEnd}
                                    >
                                        <span className={styles.itemHandle}><DotsIcon /></span>
                                        <span className={styles.itemName}>{item.ticketname}</span>
                                        {showPurchaser && (
                                            <span className={styles.itemPurchaser}>({item.transactionname})</span>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </>
                )}
            </div>
        )
    }

    // Masonry: place each group in the currently shortest column (estimated by row count)
    // so columns stack with no gaps, while keeping the default group first in column 1
    const getMasonryColumns = () => {
        const columns: any[][] = Array.from({ length: columnCount }, () => [])
        const heights = new Array(columnCount).fill(0)
        groupData.ticketgroups.forEach(group => {
            const estimate = 3 + (isGroupCollapsed(group) ? 0 : Math.max(group.guests.length, 1))
            const col = heights.indexOf(Math.min(...heights))
            columns[col].push(group)
            heights[col] += estimate
        })
        return columns
    }

    return (
        <div className={styles.root}>
            <Dialog open={dialogObj.msg.length > 0}>
                <DialogContent>
                    <DialogContentText>{dialogObj.msg}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm}>DELETE</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={dialogMsg.length > 0}>
                <DialogContent>
                    <DialogContentText>{dialogMsg}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog}>Ok</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={groupName.name.length > 0} fullWidth>
                <DialogContent>
                    <TextField
                        style={{ marginTop: '10px', width: '100%' }}
                        label="Group Name"
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button onClick={saveGroupName}>Save</Button>
                </DialogActions>
            </Dialog>

            <LoadingBackdrop open={loading} />

            <h1>Group Maintenance</h1>

            <div className={styles.toolbar}>
                <Select
                    onChange={handleSelectionChange}
                    style={{ minWidth: '140px', height: '36px', fontSize: '13px' }}
                    value={ticketItem}
                >
                    {getTicketItemOptions()}
                </Select>

                <div className={styles.toolbarGroup} title="How many people per group">
                    <label htmlFor="gm-group-size">Group size</label>
                    <input
                        id="gm-group-size"
                        type="number"
                        min={0}
                        value={groupSizeInput}
                        onChange={(e) => setGroupSizeInput(e.target.value)}
                        onBlur={commitGroupSize}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur() }}
                    />
                </div>

                <div className={styles.toolbarGroup} title="Layout mode">
                    <span>Layout</span>
                    <button
                        className={cx(styles.iconBtn, layoutStyle === 'grid' && styles.active)}
                        title="Strict grid (equal-height rows)"
                        onClick={() => setLayoutStyle('grid')}
                    >
                        <GridIcon />
                    </button>
                    <button
                        className={cx(styles.iconBtn, layoutStyle === 'masonry' && styles.active)}
                        title="Masonry (fit content)"
                        onClick={() => setLayoutStyle('masonry')}
                    >
                        <MasonryIcon />
                    </button>
                </div>

                <div className={styles.toolbarGroup} title={groupSize === 0 ? 'Group size is 0, so this collapses every group except the holding group' : undefined}>
                    <label htmlFor="gm-collapse">Collapse full</label>
                    <label className={styles.switch}>
                        <input
                            id="gm-collapse"
                            type="checkbox"
                            checked={collapseGroups}
                            onChange={(e) => setCollapseGroups(e.target.checked)}
                        />
                        <span className={styles.slider} />
                    </label>
                </div>

                <div className={styles.spacer} />
                <button className={styles.addBtn} onClick={addNewGroup}>+ Add group</button>
            </div>

            <p className={styles.hint}>
                {groupData.ticketgroups.length > 1
                    ? 'Drag the ⠿ handle on a group card to reorder groups. Drag items between groups to reassign them.'
                    : '\u00a0'}
            </p>

            {layoutStyle === 'grid' ? (
                <div className={styles.gridMode}>
                    {groupData.ticketgroups.map(group => renderGroupCard(group))}
                </div>
            ) : (
                <div className={styles.masonryMode}>
                    {getMasonryColumns().map((column, i) => (
                        <div key={i} className={styles.masonryColumn}>
                            {column.map(group => renderGroupCard(group))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export default EventGroupMaintenance
